import apiService from './api';
import type { ApiResponse } from '../types';
import type { NurseryInvoice } from '../types/nursery';
import type {
  CustomerDeposit,
  CustomerDeposits,
  DepositRequest,
  DepositsOverview,
  FoundInXero,
  OrderDeposit,
  CustomerAccount,
  CreditPosition,
  CustomerSite,
  CustomerPriceList,
  InvoiceRow,
  InvoiceDetail,
  CreditNote,
  CreateCreditNotePayload,
  CreditNoteRequest,
  CreditRequestAccess,
  CreateCreditRequestPayload,
  CreditRequestStatus,
  CreditableLine,
  CreditReason,
  Payment,
  RecordPaymentPayload,
  AgedDebtors,
  ExpectedPayments,
  ShortPay,
  Statement,
  RebateAgreement,
  CreateRebateAgreementPayload,
  RebateAccrual,
  RebateStatementSummary,
  OrderRefund,
  XeroAccountingStatus,
  XeroAccountingMapping,
  XeroAccount,
  XeroTaxRate,
  XeroFailures,
  FailedCounterPayment,
  FailedTillCash,
  XeroContactSummary,
  CustomerXeroContact,
  CustomerXeroContactFill,
  TradingTerms,
  SaveTradingTermsPayload,
  AccountRules,
  AccountRulesAccess,
  SaveAccountRulesPayload,
  OrderAccountHold,
} from '../types/nurseryAccounts';

/**
 * Nursery accounts and receivables. Backend: HHNodeServer
 * routes/nurseryReceivables.js (and the invoice routes in routes/nursery.js).
 *
 * A manager or admin seat reads all of it. Anything that moves money needs
 * the nursery admin seat; the server refuses otherwise.
 */

const query = (params: Record<string, string | number | boolean | null | undefined>) => {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
  }
  const s = q.toString();
  return s ? `?${s}` : '';
};

const blob = (url: string) => apiService.get(url, { responseType: 'blob', timeout: 60_000 }) as unknown as Promise<Blob>;

export interface InvoiceFilters {
  status?: string;
  /** 'outstanding' = authorised and unpaid; 'overdue' = those past due. */
  state?: 'outstanding' | 'overdue';
  payer_id?: number;
  customer_id?: number;
  order_id?: number;
  from?: string;
  to?: string;
  search?: string;
  xero?: 'failed';
}

class NurseryAccountsService {
  // ── Terms of trade ──

  /** The organisation's own terms, printed after its invoices and statements. */
  async getTradingTerms(): Promise<ApiResponse<TradingTerms>> {
    return apiService.get('/nursery/trading-terms');
  }

  async saveTradingTerms(payload: SaveTradingTermsPayload): Promise<ApiResponse<TradingTerms>> {
    return apiService.put('/nursery/trading-terms', payload);
  }

  // ── Account rules ──

  async getAccountRules(): Promise<ApiResponse<{ settings: AccountRules; access: AccountRulesAccess }>> {
    return apiService.get('/nursery/account-rules');
  }

  async saveAccountRules(payload: SaveAccountRulesPayload): Promise<ApiResponse<AccountRules>> {
    return apiService.put('/nursery/account-rules', payload);
  }

  /** Let a held order go: an Accounts Admin (or org admin, if the rules say so), with a note. */
  async releaseAccountHold(orderId: number, note: string): Promise<ApiResponse<OrderAccountHold>> {
    return apiService.post(`/nursery/orders/${orderId}/account-hold/release`, { note });
  }

  async undoAccountHoldRelease(orderId: number): Promise<ApiResponse<OrderAccountHold>> {
    return apiService.delete(`/nursery/orders/${orderId}/account-hold/release`);
  }

  // ── Customer deposits ──
  // Paid only ever comes from Xero, once reconciled there: nothing here records a payment.

  async getDeposits(filters: { payer_id?: number; include_void?: boolean } = {}): Promise<ApiResponse<DepositsOverview>> {
    return apiService.get(`/nursery/deposits${query({ ...filters })}`);
  }

  async getDeposit(id: number): Promise<ApiResponse<CustomerDeposit>> {
    return apiService.get(`/nursery/deposits/${id}`);
  }

  async getOrderDeposit(orderId: number): Promise<ApiResponse<OrderDeposit>> {
    return apiService.get(`/nursery/orders/${orderId}/deposit`);
  }

  /** Raise the deposit invoice for an order; it goes to Xero. */
  async requestDeposit(orderId: number, data: DepositRequest): Promise<ApiResponse<CustomerDeposit>> {
    return apiService.post(`/nursery/orders/${orderId}/deposit`, data);
  }

  /** What a customer's payer holds in deposits, and every deposit it has. */
  async getCustomerDeposits(customerId: number): Promise<ApiResponse<CustomerDeposits>> {
    return apiService.get(`/nursery/customers/${customerId}/deposits`);
  }

  /** Raise a deposit invoice for a customer with no order; it goes to Xero. */
  async requestCustomerDeposit(customerId: number, data: DepositRequest): Promise<ApiResponse<CustomerDeposit>> {
    return apiService.post(`/nursery/customers/${customerId}/deposits`, data);
  }

  async waiveDeposit(orderId: number, note: string): Promise<ApiResponse<OrderDeposit>> {
    return apiService.post(`/nursery/orders/${orderId}/deposit/waive`, { note });
  }

  async unwaiveDeposit(orderId: number): Promise<ApiResponse<OrderDeposit>> {
    return apiService.delete(`/nursery/orders/${orderId}/deposit/waive`);
  }

  /** Accounts Admin: take on invoices to Customer Deposits already in Xero (opening balances). */
  async findDepositsInXero(from: string): Promise<ApiResponse<FoundInXero>> {
    return apiService.post('/nursery/deposits/find-in-xero', { from }, undefined, { timeout: 60000 });
  }

  async voidDeposit(id: number, reason: string): Promise<ApiResponse<CustomerDeposit>> {
    return apiService.post(`/nursery/deposits/${id}/void`, { reason });
  }

  async retryDepositXero(id: number): Promise<ApiResponse<CustomerDeposit>> {
    return apiService.post(`/nursery/deposits/${id}/xero-retry`);
  }

  // ── Accounts ──

  async getSites(customerId: number): Promise<ApiResponse<CustomerSite[]>> {
    return apiService.get(`/nursery/customers/${customerId}/sites`);
  }

  /** The one list a customer is priced from, and the default beside it. */
  async getCustomerPriceList(customerId: number): Promise<ApiResponse<CustomerPriceList>> {
    return apiService.get(`/nursery/customers/${customerId}/price-list`);
  }

  async getAccount(customerId: number): Promise<ApiResponse<CustomerAccount & { credit: CreditPosition | null }>> {
    return apiService.get(`/nursery/customers/${customerId}/account`);
  }

  async getStatement(payerId: number, range: { from?: string; to?: string }): Promise<ApiResponse<Statement>> {
    return apiService.get(`/nursery/customers/${payerId}/statement${query(range)}`);
  }

  async getStatementPdf(payerId: number, range: { from?: string; to?: string }): Promise<Blob> {
    return blob(`/nursery/customers/${payerId}/statement.pdf${query(range)}`);
  }

  /** Copies of every invoice on the account for a date range, one to a page. */
  async getInvoiceCopiesPdf(payerId: number, range: { from?: string; to?: string }): Promise<Blob> {
    return blob(`/nursery/customers/${payerId}/invoices.pdf${query(range)}`);
  }

  // ── Invoices ──

  async getInvoices(filters: InvoiceFilters = {}): Promise<ApiResponse<InvoiceRow[]>> {
    return apiService.get(`/nursery/invoices${query({ ...filters })}`);
  }

  async getInvoice(id: number): Promise<ApiResponse<InvoiceDetail>> {
    return apiService.get(`/nursery/invoices/${id}`);
  }

  async getInvoicePdf(id: number): Promise<Blob> {
    return blob(`/nursery/invoices/${id}/pdf`);
  }

  async authoriseInvoice(id: number): Promise<ApiResponse<NurseryInvoice>> {
    return apiService.post(`/nursery/invoices/${id}/authorise`, {});
  }

  /** Type a draft invoice's freight (a carrier's price, or an override); null works it out again. */
  async setInvoiceFreight(id: number, amount: number | null): Promise<ApiResponse<InvoiceDetail>> {
    return apiService.put(`/nursery/invoices/${id}/freight`, { amount });
  }

  async voidInvoice(id: number, reason: string): Promise<ApiResponse<NurseryInvoice>> {
    return apiService.post(`/nursery/invoices/${id}/void`, { reason });
  }

  async openDispute(id: number, note: string): Promise<ApiResponse<NurseryInvoice>> {
    return apiService.post(`/nursery/invoices/${id}/dispute`, { note });
  }

  async resolveDispute(id: number, note: string): Promise<ApiResponse<NurseryInvoice>> {
    return apiService.post(`/nursery/invoices/${id}/dispute/resolve`, { note });
  }

  /** A credit note for what an overdue invoice still owes, which closes it. */
  async acceptShortPayment(id: number, data: { reason: CreditReason; note: string }): Promise<ApiResponse<CreditNote>> {
    return apiService.post(`/nursery/invoices/${id}/accept-short-payment`, data);
  }

  // ── Credit notes ──

  async getCreditNotes(filters: { payer_id?: number; invoice_id?: number; reason?: CreditReason; agreement_id?: number } = {}): Promise<ApiResponse<CreditNote[]>> {
    return apiService.get(`/nursery/credit-notes${query({ ...filters })}`);
  }

  async getCreditNote(id: number): Promise<ApiResponse<CreditNote>> {
    return apiService.get(`/nursery/credit-notes/${id}`);
  }

  async getCreditNotePdf(id: number): Promise<Blob> {
    return blob(`/nursery/credit-notes/${id}/pdf`);
  }

  async createCreditNote(data: CreateCreditNotePayload): Promise<ApiResponse<CreditNote>> {
    return apiService.post('/nursery/credit-notes', data);
  }

  // ── Credit note requests: a nursery manager asks, an approver decides ──

  /** Each line of an invoice and what is left to credit on it. */
  async getCreditableLines(invoiceId: number): Promise<ApiResponse<CreditableLine[]>> {
    return apiService.get(`/nursery/invoices/${invoiceId}/creditable-lines`);
  }

  async getCreditRequestAccess(): Promise<ApiResponse<CreditRequestAccess>> {
    return apiService.get('/nursery/credit-note-requests/access');
  }

  async getCreditRequests(filters: { status?: CreditRequestStatus; invoice_id?: number; order_id?: number } = {}): Promise<ApiResponse<CreditNoteRequest[]>> {
    return apiService.get(`/nursery/credit-note-requests${query({ ...filters })}`);
  }

  async requestCreditNote(data: CreateCreditRequestPayload): Promise<ApiResponse<CreditNoteRequest>> {
    return apiService.post('/nursery/credit-note-requests', data);
  }

  async approveCreditRequest(id: number, note?: string): Promise<ApiResponse<CreditNoteRequest>> {
    return apiService.post(`/nursery/credit-note-requests/${id}/approve`, { note: note ?? '' });
  }

  async declineCreditRequest(id: number, note: string): Promise<ApiResponse<CreditNoteRequest>> {
    return apiService.post(`/nursery/credit-note-requests/${id}/decline`, { note });
  }

  async withdrawCreditRequest(id: number): Promise<ApiResponse<CreditNoteRequest>> {
    return apiService.post(`/nursery/credit-note-requests/${id}/withdraw`, {});
  }

  async voidCreditNote(id: number, reason: string): Promise<ApiResponse<CreditNote>> {
    return apiService.post(`/nursery/credit-notes/${id}/void`, { reason });
  }

  // ── Payments (entered here only while Xero is not connected) ──

  async getPayments(filters: { payer_id?: number; from?: string; to?: string } = {}): Promise<ApiResponse<Payment[]>> {
    return apiService.get(`/nursery/payments${query({ ...filters })}`);
  }

  async recordPayment(data: RecordPaymentPayload): Promise<ApiResponse<Payment>> {
    return apiService.post('/nursery/payments', data);
  }

  async voidPayment(id: number, reason: string): Promise<ApiResponse<Payment>> {
    return apiService.post(`/nursery/payments/${id}/void`, { reason });
  }

  // ── Receivables ──

  async getAgedDebtors(): Promise<ApiResponse<AgedDebtors>> {
    return apiService.get('/nursery/receivables/aged');
  }

  async getExpectedPayments(filters: { payer_id?: number; by?: string } = {}): Promise<ApiResponse<ExpectedPayments>> {
    return apiService.get(`/nursery/receivables/expected${query({ ...filters })}`);
  }

  async getShortPays(payerId?: number): Promise<ApiResponse<ShortPay[]>> {
    return apiService.get(`/nursery/receivables/short-pays${query({ payer_id: payerId })}`);
  }

  // ── Rebates ──

  async getRebateAgreements(filters: { customer_id?: number; include_revoked?: boolean } = {}): Promise<ApiResponse<RebateAgreement[]>> {
    return apiService.get(`/nursery/rebates/agreements${query({ ...filters })}`);
  }

  async getRebateAgreement(id: number): Promise<ApiResponse<RebateAgreement>> {
    return apiService.get(`/nursery/rebates/agreements/${id}`);
  }

  async createRebateAgreement(data: CreateRebateAgreementPayload): Promise<ApiResponse<RebateAgreement>> {
    return apiService.post('/nursery/rebates/agreements', data);
  }

  /** Agreements are revoked and replaced, never edited. */
  async revokeRebateAgreement(id: number, reason: string): Promise<ApiResponse<RebateAgreement>> {
    return apiService.post(`/nursery/rebates/agreements/${id}/revoke`, { reason });
  }

  async getRebateAccrual(id: number, date?: string): Promise<ApiResponse<RebateAccrual>> {
    return apiService.get(`/nursery/rebates/agreements/${id}/accrual${query({ date })}`);
  }

  async closeRebatePeriod(id: number, periodStart: string): Promise<ApiResponse<{ statement: RebateStatementSummary; credit_note: CreditNote | null }>> {
    return apiService.post(`/nursery/rebates/agreements/${id}/close`, { period_start: periodStart });
  }

  // ── Refunds ──

  async retryRefund(id: number): Promise<ApiResponse<OrderRefund>> {
    return apiService.post(`/nursery/refunds/${id}/retry`, {});
  }

  // ── Xero Accounting ──
  // What invoicing does with the organisation's Xero. The connection itself
  // (sign-in, organisation, disconnect) is shared with payroll: xeroConnection.ts.

  async getXeroStatus(): Promise<ApiResponse<XeroAccountingStatus>> {
    return apiService.get('/nursery/xero-accounting/status');
  }

  async getXeroAccounts(): Promise<ApiResponse<XeroAccount[]>> {
    return apiService.get('/nursery/xero-accounting/accounts');
  }

  async getXeroTaxRates(): Promise<ApiResponse<XeroTaxRate[]>> {
    return apiService.get('/nursery/xero-accounting/tax-rates');
  }

  async saveXeroMapping(mapping: Partial<XeroAccountingMapping>): Promise<ApiResponse<XeroAccountingStatus>> {
    return apiService.put('/nursery/xero-accounting/mapping', mapping);
  }

  /** Push what is waiting, then read payments back. Xero can be slow: two minutes. */
  async syncXero(): Promise<ApiResponse<{
    status: XeroAccountingStatus;
    pushed?: { invoices: number; credit_notes: number; failed?: number };
  }>> {
    return apiService.post('/nursery/xero-accounting/sync', {}, undefined, { timeout: 120_000 });
  }

  async retryXero(target: { invoice_id?: number; credit_note_id?: number }): Promise<ApiResponse<{ queued: boolean }>> {
    return apiService.post('/nursery/xero-accounting/retry', target);
  }

  /** Queue invoices authorised before Xero was connected, from a date. */
  async backfillXero(from: string): Promise<ApiResponse<{ queued: number }>> {
    return apiService.post('/nursery/xero-accounting/backfill', { from });
  }

  async getXeroFailures(): Promise<ApiResponse<XeroFailures>> {
    return apiService.get('/nursery/xero-accounting/failures');
  }

  /** Send every till banking, over, short and cash out Xero has not taken again; returns what is still left. */
  async retryTillCash(): Promise<ApiResponse<{ till_cash: FailedTillCash[]; status: XeroAccountingStatus }>> {
    return apiService.post('/nursery/xero-accounting/till-cash/retry', {}, undefined, { timeout: 120_000 });
  }

  /** Send every counter payment Xero has not taken again; returns what is still left. */
  async retryCounterPayments(): Promise<ApiResponse<{ counter_payments: FailedCounterPayment[]; status: XeroAccountingStatus }>> {
    return apiService.post('/nursery/xero-accounting/counter-payments/retry', {}, undefined, { timeout: 120_000 });
  }

  /** Which Xero contact a customer's invoices go to. */
  async getCustomerXeroContact(customerId: number): Promise<ApiResponse<CustomerXeroContact>> {
    return apiService.get(`/nursery/customers/${customerId}/xero-contact`);
  }

  /** Link a customer to a Xero contact chosen by hand; null removes the link. */
  async setCustomerXeroContact(customerId: number, contactId: string | null): Promise<ApiResponse<CustomerXeroContact>> {
    return apiService.put(`/nursery/customers/${customerId}/xero-contact`, { contact_id: contactId });
  }

  /** Fill the linked Xero contact's blanks from this customer; nothing Xero has is changed. */
  async fillCustomerXeroContact(customerId: number): Promise<ApiResponse<CustomerXeroContactFill>> {
    return apiService.post(`/nursery/customers/${customerId}/xero-contact/fill`, {});
  }

  async searchXeroContacts(search: string): Promise<ApiResponse<XeroContactSummary[]>> {
    return apiService.get(`/nursery/xero-accounting/contacts${query({ search })}`);
  }
}

const nurseryAccountsService = new NurseryAccountsService();
export default nurseryAccountsService;
